import { useEffect, useState } from "react";

const SHARE_TITLE = "七维排课助手";
const SHARE_DESCRIPTION = "面向特殊教育学校的课程、教师、班级与功能教室排课工具";
const SHARE_IMAGE_PATH = "/brand/qivid-scheduler-share.jpg?share=wechat-v3";
const SHARE_LINK_PATH = "/?share=wechat-v3";
const WECHAT_SDK_URL = "https://res.wx.qq.com/open/js/jweixin-1.6.0.js";
const WECHAT_DIAGNOSTIC_ENDPOINT = "/api/v2/wechat/client-diagnostics";
const WECHAT_SDK_TIMEOUT_MS = 10_000;
const WECHAT_CONFIG_TIMEOUT_MS = 10_000;
const WECHAT_API_CHECK_TIMEOUT_MS = 3_000;
const WECHAT_SHARE_TIMEOUT_MS = 15_000;
const WECHAT_READY_MESSAGE = "分享内容已准备好，请点右上角发送给好友或分享到朋友圈";
const WECHAT_PENDING_MESSAGE = "正在准备分享内容，请稍候再点右上角分享";
const WECHAT_SHARE_APIS = [
  "updateAppMessageShareData",
  "updateTimelineShareData",
  "onMenuShareAppMessage",
  "onMenuShareTimeline"
] as const;

type WechatSetupState = "idle" | "pending" | "ready" | "unavailable";
type WechatDiagnosticStage =
  | "sdk_load"
  | "config_fetch"
  | "wx_config"
  | "check_js_api"
  | "legacy_friend"
  | "legacy_timeline"
  | "modern_friend"
  | "modern_timeline"
  | "share_friend"
  | "share_timeline"
  | "setup";
type WechatDiagnosticOutcome =
  | "started"
  | "success"
  | "failure"
  | "timeout"
  | "cancel"
  | "missing";
type WechatClientPlatform = "ios" | "android" | "windows" | "macos" | "other";

interface WechatJsConfig {
  appId: string;
  timestamp: number;
  nonceStr: string;
  signature: string;
}

interface WechatSdk {
  config(options: WechatJsConfig & { debug: boolean; jsApiList: string[] }): void;
  ready(callback: () => void): void;
  error(callback: (result: WechatCallResult) => void): void;
  checkJsApi?: (options: {
    jsApiList: string[];
    success: (result: WechatCheckJsApiResult) => void;
    fail: (result: WechatCallResult) => void;
  }) => void;
  updateAppMessageShareData?: WechatShareMethod;
  updateTimelineShareData?: WechatShareMethod;
  onMenuShareAppMessage?: (options: WechatLegacySharePayload) => void;
  onMenuShareTimeline?: (options: WechatLegacySharePayload) => void;
}

interface SharePayload {
  title: string;
  desc: string;
  link: string;
  imgUrl: string;
}

interface WechatCallResult {
  errMsg?: string;
}

interface WechatLegacySharePayload extends SharePayload {
  type: "link";
  dataUrl: "";
  trigger: () => void;
  success: (result: WechatCallResult) => void;
  cancel: (result: WechatCallResult) => void;
  fail: (result: WechatCallResult) => void;
}

interface WechatCheckJsApiResult extends WechatCallResult {
  checkResult?: Record<string, boolean | string>;
}

interface WechatDiagnosticEvent {
  stage: WechatDiagnosticStage;
  outcome: WechatDiagnosticOutcome;
  elapsed_ms: number;
  detail?: string;
}

interface WechatApiCapabilities {
  check_js_api: boolean;
  update_app_message_share_data: boolean;
  update_timeline_share_data: boolean;
  on_menu_share_app_message: boolean;
  on_menu_share_timeline: boolean;
}

interface WechatApiCheckResult {
  update_app_message_share_data: boolean | null;
  update_timeline_share_data: boolean | null;
  on_menu_share_app_message: boolean | null;
  on_menu_share_timeline: boolean | null;
}

interface WechatDiagnosticSession {
  schema_version: 1;
  diagnostic_id: string;
  page_path: string;
  share_path: string;
  wechat_version: string;
  client_platform: WechatClientPlatform;
  config_request_id: string | null;
  config_http_status: number | null;
  capabilities: WechatApiCapabilities;
  check_result: WechatApiCheckResult;
  events: WechatDiagnosticEvent[];
  started_at: number;
}

class WechatSetupFailure extends Error {
  constructor(message: string, readonly diagnostics: WechatDiagnosticSession) {
    super(message);
    this.name = "WechatSetupFailure";
  }
}

type WechatShareMethod = (
  options: SharePayload & {
    success: () => void;
    fail: (result: WechatCallResult) => void;
  }
) => void;

declare global {
  interface Window {
    wx?: WechatSdk;
  }
}

let wechatSdkPromise: Promise<WechatSdk> | null = null;

export function ShareButton(): React.JSX.Element {
  const [message, setMessage] = useState("");
  const [wechatSetup, setWechatSetup] = useState<WechatSetupState>("idle");
  const [wechatFailure, setWechatFailure] = useState("");
  const inWechat = isWechatBrowser();

  useEffect(() => {
    if (!inWechat) return;
    let active = true;
    setWechatSetup("pending");
    setWechatFailure("");
    void prepareWechatShare().then(
      () => {
        if (!active) return;
        setWechatSetup("ready");
        setMessage((current) =>
          current === WECHAT_PENDING_MESSAGE ? WECHAT_READY_MESSAGE : current
        );
      },
      (error: unknown) => {
        if (!active) return;
        const failureMessage = wechatFailureMessage(error);
        setWechatFailure(failureMessage);
        setWechatSetup("unavailable");
        setMessage((current) =>
          current === WECHAT_PENDING_MESSAGE ? failureMessage : current
        );
      }
    );
    return () => {
      active = false;
    };
  }, [inWechat]);

  async function share(): Promise<void> {
    const link = shareLink();
    if (inWechat) {
      setMessage(
        wechatSetup === "ready"
          ? WECHAT_READY_MESSAGE
          : wechatSetup === "pending"
            ? WECHAT_PENDING_MESSAGE
            : wechatFailure || "微信分享卡片设置失败，请刷新页面后重试"
      );
      return;
    }
    if (typeof navigator.share === "function") {
      try {
        await navigator.share({ title: SHARE_TITLE, text: SHARE_DESCRIPTION, url: link });
        setMessage("已打开系统分享面板");
        return;
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") return;
      }
    }
    try {
      await navigator.clipboard.writeText(link);
      setMessage("链接已复制，可以粘贴发送给微信好友");
    } catch {
      setMessage(`复制下面的链接发送给好友：${link}`);
    }
  }

  return (
    <div className="cover-share">
      <button
        className="cover-share-button"
        type="button"
        aria-label="分享七维排课助手"
        onClick={() => void share()}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="18" cy="5" r="3" />
          <circle cx="6" cy="12" r="3" />
          <circle cx="18" cy="19" r="3" />
          <path d="m8.6 10.6 6.8-4.1M8.6 13.4l6.8 4.1" />
        </svg>
      </button>
      {message ? (
        <p className="cover-share-message" role="status">
          {message}
        </p>
      ) : null}
    </div>
  );
}

function isWechatBrowser(): boolean {
  return /MicroMessenger/i.test(navigator.userAgent);
}

function shareLink(): string {
  return new URL(SHARE_LINK_PATH, window.location.origin).toString();
}

function sharePayload(): SharePayload {
  const link = shareLink();
  return {
    title: SHARE_TITLE,
    desc: SHARE_DESCRIPTION,
    link,
    imgUrl: new URL(SHARE_IMAGE_PATH, link).toString()
  };
}

async function prepareWechatShare(): Promise<void> {
  const diagnostics = createWechatDiagnosticSession();
  try {
    const [config, wx] = await Promise.all([
      fetchWechatConfig(diagnostics),
      loadWechatSdkWithDiagnostics(diagnostics)
    ]);
    diagnostics.capabilities = wechatCapabilities(wx);
    await configureWechatSdk(wx, config, diagnostics);
    recordWechatDiagnostic(diagnostics, "setup", "success");
    void reportWechatDiagnostics(diagnostics);
  } catch (error) {
    if (
      !diagnostics.events.some(
        (event) => event.outcome === "failure" || event.outcome === "timeout"
      )
    ) {
      recordWechatDiagnostic(diagnostics, "setup", "failure", errorDetail(error));
    }
    const failure = new WechatSetupFailure(errorDetail(error), diagnostics);
    void reportWechatDiagnostics(diagnostics);
    throw failure;
  }
}

async function fetchWechatConfig(
  diagnostics: WechatDiagnosticSession
): Promise<WechatJsConfig> {
  const pageUrl = window.location.href.split("#", 1)[0] ?? window.location.href;
  const endpoint = new URL("/api/v2/wechat/js-config", window.location.origin);
  endpoint.searchParams.set("url", pageUrl);
  recordWechatDiagnostic(diagnostics, "config_fetch", "started");
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), WECHAT_CONFIG_TIMEOUT_MS);
  try {
    const response = await fetch(endpoint, {
      headers: { accept: "application/json" },
      signal: controller.signal
    });
    diagnostics.config_http_status = response.status;
    diagnostics.config_request_id = response.headers.get("x-request-id");
    if (!response.ok) throw new Error(`wechat config http ${response.status}`);
    const loaded: unknown = await response.json();
    if (!isWechatJsConfig(loaded)) throw new Error("wechat config response invalid");
    recordWechatDiagnostic(diagnostics, "config_fetch", "success", `http ${response.status}`);
    return loaded;
  } catch (error) {
    const timeoutFailure = controller.signal.aborted;
    const detail = timeoutFailure ? "wechat config fetch:timeout" : errorDetail(error);
    recordWechatDiagnostic(
      diagnostics,
      "config_fetch",
      timeoutFailure ? "timeout" : "failure",
      detail
    );
    throw new Error(detail);
  } finally {
    window.clearTimeout(timeout);
  }
}

async function loadWechatSdkWithDiagnostics(
  diagnostics: WechatDiagnosticSession
): Promise<WechatSdk> {
  recordWechatDiagnostic(diagnostics, "sdk_load", "started");
  try {
    const wx = await loadWechatSdk();
    recordWechatDiagnostic(diagnostics, "sdk_load", "success");
    return wx;
  } catch (error) {
    const detail = errorDetail(error);
    recordWechatDiagnostic(
      diagnostics,
      "sdk_load",
      /timeout/i.test(detail) ? "timeout" : "failure",
      detail
    );
    throw error;
  }
}

function configureWechatSdk(
  wx: WechatSdk,
  config: WechatJsConfig,
  diagnostics: WechatDiagnosticSession
): Promise<void> {
  recordWechatDiagnostic(diagnostics, "wx_config", "started");
  return new Promise<void>((resolve, reject) => {
    let configSettled = false;
    const finishConfig = (callback: () => void): void => {
      if (configSettled) return;
      configSettled = true;
      window.clearTimeout(timeout);
      callback();
    };
    const timeout = window.setTimeout(
      () =>
        finishConfig(() => {
          const detail = "wx.config:timeout";
          recordWechatDiagnostic(diagnostics, "wx_config", "timeout", detail);
          reject(new Error(detail));
        }),
      WECHAT_CONFIG_TIMEOUT_MS
    );
    try {
      wx.ready(() =>
        finishConfig(() => {
          recordWechatDiagnostic(diagnostics, "wx_config", "success");
          const payload = sharePayload();
          const legacy = registerLegacyShareHandlers(wx, payload, diagnostics);
          void Promise.all([
            checkWechatApis(wx, diagnostics),
            configureCurrentShareHandlers(wx, payload, legacy, diagnostics)
          ])
            .then(([, result]) => result)
            .then(resolve, reject);
        })
      );
      wx.error((result) =>
        finishConfig(() => {
          const detail = result.errMsg || "wechat sdk configuration failed";
          recordWechatDiagnostic(diagnostics, "wx_config", "failure", detail);
          reject(new Error(detail));
        })
      );
      wx.config({
        debug: false,
        appId: config.appId,
        timestamp: config.timestamp,
        nonceStr: config.nonceStr,
        signature: config.signature,
        jsApiList: ["checkJsApi", ...WECHAT_SHARE_APIS]
      });
    } catch (error) {
      finishConfig(() => {
        const detail = errorDetail(error);
        recordWechatDiagnostic(diagnostics, "wx_config", "failure", detail);
        reject(error);
      });
    }
  });
}

function registerLegacyShareHandlers(
  wx: WechatSdk,
  payload: SharePayload,
  diagnostics: WechatDiagnosticSession
): { friend: boolean; timeline: boolean } {
  return {
    friend: registerLegacyShareHandler(
      wx,
      wx.onMenuShareAppMessage,
      payload,
      "legacy_friend",
      diagnostics
    ),
    timeline: registerLegacyShareHandler(
      wx,
      wx.onMenuShareTimeline,
      payload,
      "legacy_timeline",
      diagnostics
    )
  };
}

function registerLegacyShareHandler(
  wx: WechatSdk,
  method: ((options: WechatLegacySharePayload) => void) | undefined,
  payload: SharePayload,
  stage: "legacy_friend" | "legacy_timeline",
  diagnostics: WechatDiagnosticSession
): boolean {
  if (!method) {
    recordWechatDiagnostic(diagnostics, stage, "missing");
    return false;
  }
  recordWechatDiagnostic(diagnostics, stage, "started");
  const shareStage = stage === "legacy_friend" ? "share_friend" : "share_timeline";
  try {
    method.call(wx, {
      ...payload,
      type: "link",
      dataUrl: "",
      trigger: () => {
        recordWechatDiagnostic(diagnostics, shareStage, "started");
        void reportWechatDiagnostics(diagnostics);
      },
      success: (result) => {
        recordWechatDiagnostic(
          diagnostics,
          shareStage,
          "success",
          result.errMsg || `${shareStage}:ok`
        );
        void reportWechatDiagnostics(diagnostics);
      },
      cancel: (result) => {
        recordWechatDiagnostic(
          diagnostics,
          shareStage,
          "cancel",
          result.errMsg || `${shareStage}:cancel`
        );
        void reportWechatDiagnostics(diagnostics);
      },
      fail: (result) => {
        recordWechatDiagnostic(
          diagnostics,
          shareStage,
          "failure",
          result.errMsg || `${shareStage}:fail`
        );
        void reportWechatDiagnostics(diagnostics);
      }
    });
    recordWechatDiagnostic(diagnostics, stage, "success");
    return true;
  } catch (error) {
    recordWechatDiagnostic(diagnostics, stage, "failure", errorDetail(error));
    return false;
  }
}

function checkWechatApis(
  wx: WechatSdk,
  diagnostics: WechatDiagnosticSession
): Promise<void> {
  const method = wx.checkJsApi;
  if (!method) {
    recordWechatDiagnostic(diagnostics, "check_js_api", "missing");
    return Promise.resolve();
  }
  recordWechatDiagnostic(diagnostics, "check_js_api", "started");
  return new Promise<void>((resolve) => {
    let settled = false;
    const finish = (callback: () => void): void => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      callback();
      resolve();
    };
    const timeout = window.setTimeout(
      () =>
        finish(() =>
          recordWechatDiagnostic(
            diagnostics,
            "check_js_api",
            "timeout",
            "checkJsApi:timeout"
          )
        ),
      WECHAT_API_CHECK_TIMEOUT_MS
    );
    try {
      method.call(wx, {
        jsApiList: [...WECHAT_SHARE_APIS],
        success: (result) =>
          finish(() => {
            diagnostics.check_result = normalizeWechatCheckResult(result.checkResult);
            recordWechatDiagnostic(diagnostics, "check_js_api", "success");
          }),
        fail: (result) =>
          finish(() =>
            recordWechatDiagnostic(
              diagnostics,
              "check_js_api",
              "failure",
              result.errMsg || "checkJsApi:fail"
            )
          )
      });
    } catch (error) {
      finish(() =>
        recordWechatDiagnostic(diagnostics, "check_js_api", "failure", errorDetail(error))
      );
    }
  });
}

async function configureCurrentShareHandlers(
  wx: WechatSdk,
  payload: SharePayload,
  legacy: { friend: boolean; timeline: boolean },
  diagnostics: WechatDiagnosticSession
): Promise<void> {
  const appMessageMethod = wx.updateAppMessageShareData;
  const timelineMethod = wx.updateTimelineShareData;
  if (!appMessageMethod || !timelineMethod) {
    if (!appMessageMethod) {
      recordWechatDiagnostic(diagnostics, "modern_friend", "missing");
    }
    if (!timelineMethod) {
      recordWechatDiagnostic(diagnostics, "modern_timeline", "missing");
    }
    if (legacy.friend && legacy.timeline) return;
    throw new Error("wechat share function not exist");
  }
  const results = await Promise.allSettled([
    callWechatShareMethod(
      wx,
      appMessageMethod,
      payload,
      "updateAppMessageShareData",
      "modern_friend",
      diagnostics
    ),
    callWechatShareMethod(
      wx,
      timelineMethod,
      payload,
      "updateTimelineShareData",
      "modern_timeline",
      diagnostics
    )
  ]);
  const failure = results.find((result) => result.status === "rejected");
  if (failure?.status === "rejected") throw failure.reason;
}

function callWechatShareMethod(
  wx: WechatSdk,
  method: WechatShareMethod,
  payload: SharePayload,
  methodName: string,
  stage: "modern_friend" | "modern_timeline",
  diagnostics: WechatDiagnosticSession
): Promise<void> {
  recordWechatDiagnostic(diagnostics, stage, "started");
  return new Promise<void>((resolve, reject) => {
    let settled = false;
    const finish = (callback: () => void): void => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      callback();
    };
    const timeout = window.setTimeout(
      () =>
        finish(() => {
          const detail = `${methodName}:timeout`;
          recordWechatDiagnostic(diagnostics, stage, "timeout", detail);
          reject(new Error(detail));
        }),
      WECHAT_SHARE_TIMEOUT_MS
    );
    try {
      method.call(wx, {
        ...payload,
        success: () =>
          finish(() => {
            recordWechatDiagnostic(diagnostics, stage, "success");
            resolve();
          }),
        fail: (result) =>
          finish(() => {
            const detail = result.errMsg || `${methodName}:fail`;
            recordWechatDiagnostic(diagnostics, stage, "failure", detail);
            reject(new Error(detail));
          })
      });
    } catch (error) {
      finish(() => {
        recordWechatDiagnostic(diagnostics, stage, "failure", errorDetail(error));
        reject(error);
      });
    }
  });
}

function wechatFailureMessage(error: unknown): string {
  const detail = error instanceof Error ? error.message : "";
  const safeDetail = sanitizeWechatErrorDetail(detail);
  const diagnosticId = error instanceof WechatSetupFailure
    ? error.diagnostics.diagnostic_id
    : "";
  if (/permission denied/i.test(detail)) {
    return appendWechatDiagnostic(
      "当前公众号没有网页分享权限，请检查公众号认证和接口权限",
      safeDetail,
      diagnosticId
    );
  }
  if (/invalid signature|invalid url domain/i.test(detail)) {
    return appendWechatDiagnostic(
      "微信签名校验失败，请关闭页面后从微信重新打开",
      safeDetail,
      diagnosticId
    );
  }
  if (/function not exist/i.test(detail)) {
    return appendWechatDiagnostic(
      "当前微信版本不支持分享卡片，请升级微信后重试",
      safeDetail,
      diagnosticId
    );
  }
  if (/timeout/i.test(detail)) {
    return appendWechatDiagnostic(
      "微信没有确认分享卡片设置，请刷新页面后重试",
      safeDetail,
      diagnosticId
    );
  }
  return appendWechatDiagnostic(
    "微信分享卡片设置失败，请刷新页面后重试",
    safeDetail,
    diagnosticId
  );
}

function appendWechatDiagnostic(
  message: string,
  detail: string,
  diagnosticId: string
): string {
  const parts = [
    detail ? `错误：${detail}` : "",
    diagnosticId ? `诊断编号：${diagnosticId}` : ""
  ].filter(Boolean);
  return parts.length ? `${message}（${parts.join("；")}）` : message;
}

function sanitizeWechatErrorDetail(detail: string): string {
  return detail
    .replace(/[\u0000-\u001f\u007f]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 160);
}

function errorDetail(error: unknown): string {
  return sanitizeWechatErrorDetail(error instanceof Error ? error.message : String(error));
}

function createWechatDiagnosticSession(): WechatDiagnosticSession {
  return {
    schema_version: 1,
    diagnostic_id: createWechatDiagnosticId(),
    page_path: relativeUrl(window.location.href),
    share_path: relativeUrl(shareLink()),
    wechat_version: navigator.userAgent.match(/MicroMessenger\/([0-9.]+)/i)?.[1] ?? "",
    client_platform: wechatClientPlatform(navigator.userAgent),
    config_request_id: null,
    config_http_status: null,
    capabilities: {
      check_js_api: false,
      update_app_message_share_data: false,
      update_timeline_share_data: false,
      on_menu_share_app_message: false,
      on_menu_share_timeline: false
    },
    check_result: {
      update_app_message_share_data: null,
      update_timeline_share_data: null,
      on_menu_share_app_message: null,
      on_menu_share_timeline: null
    },
    events: [],
    started_at: performance.now()
  };
}

function createWechatDiagnosticId(): string {
  const bytes = new Uint8Array(6);
  window.crypto.getRandomValues(bytes);
  const suffix = Array.from(bytes, (value) => value.toString(16).padStart(2, "0"))
    .join("")
    .toUpperCase();
  return `WX-${suffix}`;
}

function relativeUrl(value: string): string {
  const url = new URL(value, window.location.origin);
  return `${url.pathname}${url.search}`.slice(0, 512);
}

function wechatClientPlatform(userAgent: string): WechatClientPlatform {
  if (/iPhone|iPad|iPod/i.test(userAgent)) return "ios";
  if (/Android/i.test(userAgent)) return "android";
  if (/Windows/i.test(userAgent)) return "windows";
  if (/Macintosh|Mac OS X/i.test(userAgent)) return "macos";
  return "other";
}

function wechatCapabilities(wx: WechatSdk): WechatApiCapabilities {
  return {
    check_js_api: typeof wx.checkJsApi === "function",
    update_app_message_share_data: typeof wx.updateAppMessageShareData === "function",
    update_timeline_share_data: typeof wx.updateTimelineShareData === "function",
    on_menu_share_app_message: typeof wx.onMenuShareAppMessage === "function",
    on_menu_share_timeline: typeof wx.onMenuShareTimeline === "function"
  };
}

function normalizeWechatCheckResult(
  result: Record<string, boolean | string> | undefined
): WechatApiCheckResult {
  return {
    update_app_message_share_data: checkedWechatApi(result, "updateAppMessageShareData"),
    update_timeline_share_data: checkedWechatApi(result, "updateTimelineShareData"),
    on_menu_share_app_message: checkedWechatApi(result, "onMenuShareAppMessage"),
    on_menu_share_timeline: checkedWechatApi(result, "onMenuShareTimeline")
  };
}

function checkedWechatApi(
  result: Record<string, boolean | string> | undefined,
  name: string
): boolean | null {
  const value = result?.[name];
  if (typeof value === "boolean") return value;
  if (typeof value === "string") return /^(ok|true)$/i.test(value);
  return null;
}

function recordWechatDiagnostic(
  diagnostics: WechatDiagnosticSession,
  stage: WechatDiagnosticStage,
  outcome: WechatDiagnosticOutcome,
  detail?: string
): void {
  if (diagnostics.events.length >= 32) return;
  const cleaned = detail ? sanitizeWechatErrorDetail(detail).slice(0, 240) : "";
  diagnostics.events.push({
    stage,
    outcome,
    elapsed_ms: Math.max(0, Math.round(performance.now() - diagnostics.started_at)),
    ...(cleaned ? { detail: cleaned } : {})
  });
}

async function reportWechatDiagnostics(diagnostics: WechatDiagnosticSession): Promise<void> {
  const { started_at: _startedAt, ...payload } = diagnostics;
  try {
    const response = await fetch(WECHAT_DIAGNOSTIC_ENDPOINT, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true
    });
    if (!response.ok) throw new Error(`diagnostic report http ${response.status}`);
  } catch {
    console.warn(`微信分享诊断上报失败：${diagnostics.diagnostic_id}`);
  }
}

function isWechatJsConfig(value: unknown): value is WechatJsConfig {
  if (typeof value !== "object" || value === null) return false;
  const config = value as Partial<WechatJsConfig>;
  return (
    typeof config.appId === "string" && config.appId.length > 0 &&
    typeof config.timestamp === "number" && Number.isFinite(config.timestamp) &&
    typeof config.nonceStr === "string" && config.nonceStr.length > 0 &&
    typeof config.signature === "string" && /^[0-9a-f]{40}$/i.test(config.signature)
  );
}

function loadWechatSdk(): Promise<WechatSdk> {
  if (window.wx) return Promise.resolve(window.wx);
  if (wechatSdkPromise) return wechatSdkPromise;
  wechatSdkPromise = new Promise<WechatSdk>((resolve, reject) => {
    let settled = false;
    const script = document.createElement("script");
    script.src = WECHAT_SDK_URL;
    script.async = true;
    script.dataset.wechatSdk = "true";
    const finish = (callback: () => void): void => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      callback();
    };
    const fail = (message: string): void =>
      finish(() => {
        wechatSdkPromise = null;
        reject(new Error(message));
      });
    const timeout = window.setTimeout(
      () => fail("wechat sdk load:timeout"),
      WECHAT_SDK_TIMEOUT_MS
    );
    script.addEventListener("load", () => {
      if (window.wx) finish(() => resolve(window.wx as WechatSdk));
      else fail("wechat sdk did not initialize");
    });
    script.addEventListener("error", () => fail("wechat sdk unavailable"));
    try {
      document.head.append(script);
    } catch (error) {
      fail(errorDetail(error));
    }
  });
  return wechatSdkPromise;
}
